
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, FileText } from 'lucide-react';

interface Provider {
  id: string;
  provider: { 
    nombre: string; 
    tipo: string;
    telefono?: string;
    email?: string;
  };
  descripcion_servicio: string;
  precio: number;
  pagado: number;
  notas?: string;
}

interface ProviderInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: Provider | null;
  onSaveNotes: (providerId: string, notes: string) => void;
}

export const ProviderInfoModal: React.FC<ProviderInfoModalProps> = ({
  open,
  onOpenChange,
  provider,
  onSaveNotes
}) => {
  const [notes, setNotes] = useState(provider?.notas || '');

  React.useEffect(() => {
    setNotes(provider?.notas || '');
  }, [provider]);

  const handleSaveNotes = () => {
    if (provider) {
      onSaveNotes(provider.id, notes);
      onOpenChange(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getPaymentStatus = (precio: number, pagado: number) => {
    const percentage = (pagado / precio) * 100;
    if (percentage === 100) return <Badge className="bg-emerald-100 text-emerald-800">Pagado</Badge>;
    if (percentage > 0) return <Badge className="bg-amber-100 text-amber-800">Parcial</Badge>;
    return <Badge className="bg-red-100 text-red-800">Pendiente</Badge>;
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Información del Proveedor
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Información básica */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg text-gray-900 mb-2">
              {provider.provider.nombre}
            </h3>
            <p className="text-gray-600 mb-3">{provider.provider.tipo}</p>
            
            <div className="space-y-2">
              {provider.provider.telefono && (
                <div className="flex items-center text-sm">
                  <Phone className="w-4 h-4 mr-2 text-gray-500" />
                  <span>{provider.provider.telefono}</span>
                </div>
              )}
              {provider.provider.email && (
                <div className="flex items-center text-sm">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" />
                  <span>{provider.provider.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Servicio y precio */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Servicio contratado</h4>
            <p className="text-gray-600 mb-4">{provider.descripcion_servicio}</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Precio total</p>
                <p className="font-semibold text-lg">{formatCurrency(provider.precio)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Pagado</p>
                <p className="font-semibold text-lg">{formatCurrency(provider.pagado)}</p>
              </div>
            </div>
            
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-gray-500">Estado de pago:</span>
              {getPaymentStatus(provider.precio, provider.pagado)}
            </div>
            
            <div className="mt-2">
              <p className="text-sm text-gray-500">Saldo pendiente</p>
              <p className="font-semibold text-red-600">
                {formatCurrency(provider.precio - provider.pagado)}
              </p>
            </div>
          </div>

          {/* Notas */}
          <div>
            <Label htmlFor="provider_notes">Notas para este evento</Label>
            <Textarea
              id="provider_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agrega notas específicas para este proveedor en este evento..."
              rows={4}
              className="mt-2"
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveNotes}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Guardar Notas
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
